// "Deadline überschritten" — der erzwungene Owner-Dialog. Geht beim Einloggen/
// Arbeiten auf, sobald ein eigenes Projekt seine Deadline gerissen hat. Der Owner
// MUSS Stellung nehmen: entweder eine neue Deadline mit Begründung setzen oder
// das Projekt mit Begründung abschließen. Innerhalb der Sitzung verschiebbar
// ("Später erinnern"); ab dem nächsten Login wieder Pflicht, bis geklärt.
import { useEffect, useMemo, useState } from 'react';
import { useData } from './DataContext.jsx';
import { MentionTextarea } from './mention-input.jsx';
import { IconX, IconCalendar, IconCheck } from './icons.jsx';
import { fmtDeadline, fmtMin, todayIso, pad } from './lib/format.js';
import { overdueDays } from './lib/deadlines.js';

const MIN_REASON = 20;

// Tomorrow as yyyy-mm-dd — earliest selectable new deadline.
function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function DeadlineOverrunModal({ project, queueInfo, dismissable, onSubmitted, onSnooze }) {
  const { acknowledgeDeadlineOverrun, customerById, projectUsage, profiles, syncMentions } = useData();

  const customer = project.customer_id ? customerById(project.customer_id) : null;
  const daysLate = overdueDays(project.deadline);

  const wasOverBudget = useMemo(() => {
    const used = projectUsage[project.id]?.minutes || 0;
    return !!project.budget_minutes && used > project.budget_minutes;
  }, [projectUsage, project.id, project.budget_minutes]);
  const overByMin = useMemo(() => {
    const used = projectUsage[project.id]?.minutes || 0;
    return wasOverBudget ? used - project.budget_minutes : 0;
  }, [projectUsage, project.id, project.budget_minutes, wasOverBudget]);

  const [resolution, setResolution] = useState('rescheduled'); // 'rescheduled' | 'closed'
  const [newDeadline, setNewDeadline] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Escape only snoozes while still dismissable; otherwise it's a hard block.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && dismissable) onSnooze?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [dismissable, onSnooze]);

  const reasonOk = reason.trim().length >= MIN_REASON;
  const dateOk = resolution !== 'rescheduled' || (!!newDeadline && newDeadline > todayIso());
  const canSubmit = reasonOk && dateOk && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true); setErr(null);
    try {
      await acknowledgeDeadlineOverrun(project.id, {
        reason,
        resolution,
        newDeadline: resolution === 'rescheduled' ? newDeadline : null,
        wasOverBudget,
      });
      await syncMentions({
        text: reason,
        sourceType: 'deadline_overrun',
        sourceId: project.id,
        projectId: project.id,
      });
      onSubmitted?.(resolution);
    } catch (e) {
      const map = {
        new_deadline_must_be_future: 'Die neue Deadline muss in der Zukunft liegen.',
        new_deadline_required: 'Bitte eine neue Deadline wählen.',
        deadline_reason_required: 'Bitte einen Grund eintragen.',
        not_allowed: 'Keine Berechtigung für dieses Projekt.',
      };
      setErr(map[e.message] || e.message || 'Fehler beim Speichern');
      setSaving(false);
    }
  }

  const closing = resolution === 'closed';

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && dismissable) onSnooze?.(); }}>
      <div className="modal" style={{ width: 580 }}>
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--crit)' }}>
              <IconCalendar size={12}/> Deadline überschritten
              {queueInfo && queueInfo.total > 1 && (
                <span className="muted" style={{ fontWeight: 400 }}> · {queueInfo.index + 1}/{queueInfo.total}</span>
              )}
            </div>
            <h2 className="serif" style={{ fontSize: 21, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
              {project.title}
            </h2>
            <div className="tiny muted" style={{ marginTop: 4 }}>{customer?.name || 'Ohne Kunde'}</div>
          </div>
          {dismissable && (
            <button className="icon-btn" onClick={() => onSnooze?.()} title="Später erinnern"><IconX size={14}/></button>
          )}
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {/* Verzugs-Fakten */}
          <div style={{
            display: 'flex', gap: 12, padding: 14, marginBottom: 20,
            background: 'var(--crit-soft)', border: '1px solid var(--crit)', borderRadius: 10,
          }}>
            <div style={{ flex: 1 }}>
              <div className="tiny muted">Fällig war</div>
              <div className="serif" style={{ fontSize: 17, fontWeight: 500, marginTop: 2 }}>{fmtDeadline(project.deadline)}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div className="tiny muted">Verzug</div>
              <div className="serif" style={{ fontSize: 17, fontWeight: 500, marginTop: 2, color: 'var(--crit)' }}>
                {daysLate} {daysLate === 1 ? 'Tag' : 'Tage'}
              </div>
            </div>
            {project.original_deadline && project.original_deadline !== project.deadline && (
              <div style={{ flex: 1 }}>
                <div className="tiny muted">Ursprünglich</div>
                <div className="serif" style={{ fontSize: 17, fontWeight: 500, marginTop: 2 }}>{fmtDeadline(project.original_deadline)}</div>
              </div>
            )}
          </div>

          <div className="tiny" style={{ color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 18 }}>
            Dieses Projekt hat seine Deadline überschritten. Bitte nimm Stellung: Setze eine
            <b> neue Deadline</b> oder <b>schließe das Projekt ab</b> — in beiden Fällen mit Begründung.
          </div>

          {/* Aktion wählen */}
          <div className="field" style={{ marginBottom: 18 }}>
            <label>Wie gehst du vor? *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { k: 'rescheduled', label: 'Neue Deadline setzen' },
                { k: 'closed', label: 'Projekt abschließen' },
              ].map(o => {
                const active = resolution === o.k;
                return (
                  <button key={o.k} onClick={() => setResolution(o.k)} className="btn"
                          style={{
                            flex: 1, justifyContent: 'center',
                            borderColor: active ? 'var(--accent)' : 'var(--stroke)',
                            background: active ? 'var(--accent-soft)' : 'transparent',
                            color: active ? 'var(--accent)' : 'var(--ink-2)',
                            fontWeight: active ? 600 : 500,
                          }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {resolution === 'rescheduled' && (
            <div className="field" style={{ marginBottom: 18 }}>
              <label>Neue Deadline *</label>
              <input className="input" type="date" value={newDeadline} min={tomorrowIso()}
                     onChange={e => setNewDeadline(e.target.value)}/>
              {newDeadline && newDeadline <= todayIso() && (
                <div className="tiny" style={{ color: 'var(--crit)', marginTop: 4 }}>Muss in der Zukunft liegen.</div>
              )}
            </div>
          )}

          {closing && wasOverBudget && (
            <div className="tiny" style={{ color: 'var(--warn)', marginBottom: 14, lineHeight: 1.5 }}>
              Hinweis: Dieses Projekt ist {fmtMin(overByMin)} über Budget — es wird als „über Budget abgeschlossen" markiert.
            </div>
          )}

          <div className="field">
            <label>
              {closing ? 'Begründung für Abschluss & Verzug *' : 'Grund für den Deadline-Verzug *'}
              <span className="tiny muted" style={{ marginLeft: 8, fontWeight: 500 }}>
                {reason.trim().length < MIN_REASON ? `noch ${MIN_REASON - reason.trim().length} Zeichen` : '✓'}
              </span>
            </label>
            <MentionTextarea value={reason} onChange={setReason} profiles={profiles}
                      placeholder={closing
                        ? 'Warum wurde die Deadline überschritten und warum wird jetzt abgeschlossen?'
                        : 'Warum wurde die Deadline überschritten? Was ist der Plan bis zur neuen Deadline?'}
                      style={{ minHeight: 110 }}/>
          </div>

          {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{err}</div>}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          {dismissable ? (
            <button className="btn btn-ghost" onClick={() => onSnooze?.()} disabled={saving}>Später erinnern</button>
          ) : (
            <span className="tiny" style={{ color: 'var(--crit)' }}>Stellungnahme erforderlich.</span>
          )}
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}
                  style={{ opacity: canSubmit ? 1 : 0.45 }}>
            {saving ? 'Speichere…' : closing
              ? <><IconCheck size={13}/> Abschließen & dokumentieren</>
              : 'Neue Deadline speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { DeadlineOverrunModal };
